import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import SafeHtml from './SafeHtml';
import { isHtmlLike } from '../lib/sanitizeHtml';
import { loadSiteContentPage } from '../lib/siteContent';
import { optimizedLogoUrl } from '../lib/assetUrl';

function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();
  const [content, setContent] = useState({
    logo_url: '/logo.png',
    explore_title: 'Explore',
    explore_library: 'Free Library',
    explore_products: 'Digital Products',
    explore_premium: 'Premium',
    company_title: 'Company',
    company_about: 'About Us',
    company_contact: 'Contact',
    legal_title: 'Legal',
    legal_privacy: 'Privacy Policy',
    legal_terms: 'Terms of Service',
    legal_affiliate: 'Affiliate Disclosure',
    admin_title: 'Admin',
    admin_login: 'Admin Login',
    admin_dashboard: 'Dashboard',
    copyright_site: 'TinyPDFs.com',
    tagline: 'Big value in tiny PDFs. Built for creators, entrepreneurs, and lifelong learners.'
  });

  const [contentTypeByKey, setContentTypeByKey] = useState({});

  const isHome = location.pathname === '/';

  const scrollToSection = (sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const goToSection = (sectionId) => {
    if (isHome) {
      scrollToSection(sectionId);
      return;
    }
    navigate('/', { state: { scrollTo: sectionId } });
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const items = await loadSiteContentPage('site_footer');
      const contentMap = {};
      const typeMap = {};

      items.forEach((item) => {
        const normalizedType = String(item.content_type || '').toLowerCase();
        const inferredType = (!normalizedType || normalizedType === 'text') && isHtmlLike(item.value) ? 'html' : normalizedType;
        typeMap[item.key] = inferredType;
        contentMap[item.key] = item.value;
      });

      setContent((prev) => ({ ...prev, ...contentMap }));
      setContentTypeByKey((prev) => ({ ...prev, ...typeMap }));
    } catch (error) {
      console.error('Error fetching footer content:', error);
    }
  };

  return (
    <footer className="bg-background border-t border-border py-16 px-4" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Col 1: Library & Products */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground">{content.explore_title}</h4>
            <ul className="space-y-3">
              <li>
                <RouterLink
                  to="/pdfs"
                  className="text-sm text-muted-foreground hover:text-orange-500 cursor-pointer"
                >
                  {content.explore_library}
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/products"
                  className="text-sm text-muted-foreground hover:text-orange-500 cursor-pointer"
                >
                  {content.explore_products}
                </RouterLink>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => goToSection('premium')}
                  className="text-sm text-muted-foreground hover:text-orange-500 cursor-pointer"
                >
                  {content.explore_premium}
                </button>
              </li>
            </ul>
          </div>

          {/* Col 2: Company */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground">{content.company_title}</h4>
            <ul className="space-y-3">
              <li>
                <RouterLink to="/about" className="text-sm text-muted-foreground hover:text-orange-500">
                  {content.company_about}
                </RouterLink>
              </li>
              <li>
                <RouterLink to="/about#editorial-standards" className="text-sm text-muted-foreground hover:text-orange-500">
                  Editorial Standards
                </RouterLink>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => goToSection('contact')}
                  className="text-sm text-muted-foreground hover:text-orange-500 cursor-pointer"
                >
                  {content.company_contact}
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3: Legal */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground">{content.legal_title}</h4>
            <ul className="space-y-3">
              <li>
                <RouterLink
                  to="/privacy-policy"
                  state={{ backgroundLocation: location }}
                  className="text-sm text-muted-foreground hover:text-orange-500"
                >
                  {content.legal_privacy}
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/terms-of-service"
                  state={{ backgroundLocation: location }}
                  className="text-sm text-muted-foreground hover:text-orange-500"
                >
                  {content.legal_terms}
                </RouterLink>
              </li>
              <li>
                <RouterLink
                  to="/affiliate-disclosure"
                  state={{ backgroundLocation: location }}
                  className="text-sm text-muted-foreground hover:text-orange-500"
                >
                  {content.legal_affiliate}
                </RouterLink>
              </li>
            </ul>
          </div>

          {/* Col 4: Admin */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground">{content.admin_title}</h4>
            <ul className="space-y-3">
              <li><RouterLink to="/admin/login" className="text-sm text-muted-foreground hover:text-orange-500" data-testid="admin-login-link">{content.admin_login}</RouterLink></li>
              <li><RouterLink to="/admin/dashboard" className="text-sm text-muted-foreground hover:text-orange-500">{content.admin_dashboard}</RouterLink></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border pt-8 flex flex-col sm:flex-row justify-between items-center">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <img 
              src={optimizedLogoUrl(content.logo_url)}
              alt="TinyPDFs Logo" 
              className="h-12"
              width="90"
              height="48"
              loading="lazy"
              decoding="async"
            />
            <p className="text-sm text-muted-foreground">
              © {currentYear} {content.copyright_site}
            </p>
          </div>
          <p className="text-xs text-muted-foreground/70 text-center sm:text-right">
            {contentTypeByKey.tagline === 'html' ? (
              <SafeHtml html={content.tagline} as="span" />
            ) : (
              content.tagline
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
